/**
 * Small line diff (LCS) for comparing prompt revisions, plus a field-level
 * comparison of agent revisions. Prompts are at most a few thousand lines,
 * so the O(n·m) table is fine; above a safety limit we fall back to a
 * "replace everything" diff instead of blocking the UI.
 */
import type { AgentRevision } from "@/api/generated/AgentRevision"

export type DiffOp = "equal" | "add" | "remove"

export interface DiffLine {
  op: DiffOp
  text: string
  /** 1-based line number in the old text (equal/remove). */
  oldLine?: number
  /** 1-based line number in the new text (equal/add). */
  newLine?: number
}

const MAX_CELLS = 4_000_000

export function diffLines(before: string, after: string): DiffLine[] {
  const a = before === "" ? [] : before.replace(/\r\n/g, "\n").split("\n")
  const b = after === "" ? [] : after.replace(/\r\n/g, "\n").split("\n")

  // trim common prefix/suffix to keep the table small
  let start = 0
  while (start < a.length && start < b.length && a[start] === b[start]) start++
  let endA = a.length
  let endB = b.length
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--
    endB--
  }

  const out: DiffLine[] = []
  for (let i = 0; i < start; i++) out.push({ op: "equal", text: a[i]!, oldLine: i + 1, newLine: i + 1 })

  const midA = a.slice(start, endA)
  const midB = b.slice(start, endB)
  const n = midA.length
  const m = midB.length

  if (n * m > MAX_CELLS) {
    for (let i = 0; i < midA.length; i++) out.push({ op: "remove", text: midA[i]!, oldLine: start + i + 1 })
    for (let j = 0; j < midB.length; j++) out.push({ op: "add", text: midB[j]!, newLine: start + j + 1 })
  } else {
    // lcs[i][j] = LCS length of midA[i..] and midB[j..]
    const lcs: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1))
    for (let i = n - 1; i >= 0; i--)
      for (let j = m - 1; j >= 0; j--)
        lcs[i]![j] =
          midA[i] === midB[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!)
    let i = 0
    let j = 0
    while (i < n || j < m) {
      if (i < n && j < m && midA[i] === midB[j]) {
        out.push({ op: "equal", text: midA[i]!, oldLine: start + i + 1, newLine: start + j + 1 })
        i++
        j++
      } else if (i < n && (j >= m || lcs[i + 1]![j]! >= lcs[i]![j + 1]!)) {
        out.push({ op: "remove", text: midA[i]!, oldLine: start + i + 1 })
        i++
      } else {
        out.push({ op: "add", text: midB[j]!, newLine: start + j + 1 })
        j++
      }
    }
  }

  const tailOffsetA = endA
  const tailOffsetB = endB
  for (let k = 0; k < a.length - endA; k++)
    out.push({
      op: "equal",
      text: a[tailOffsetA + k]!,
      oldLine: tailOffsetA + k + 1,
      newLine: tailOffsetB + k + 1,
    })
  return out
}

export function diffStats(lines: DiffLine[]): { added: number; removed: number } {
  let added = 0
  let removed = 0
  for (const l of lines) {
    if (l.op === "add") added++
    else if (l.op === "remove") removed++
  }
  return { added, removed }
}

export interface DiffHunkGap {
  op: "gap"
  count: number
}

/** Collapse long runs of unchanged lines, keeping `context` lines around changes. */
export function collapseUnchanged(lines: DiffLine[], context = 3): (DiffLine | DiffHunkGap)[] {
  const keep = new Array<boolean>(lines.length).fill(false)
  lines.forEach((l, i) => {
    if (l.op === "equal") return
    for (let k = Math.max(0, i - context); k <= Math.min(lines.length - 1, i + context); k++) keep[k] = true
  })
  const out: (DiffLine | DiffHunkGap)[] = []
  let gap = 0
  lines.forEach((l, i) => {
    if (keep[i]) {
      if (gap) out.push({ op: "gap", count: gap })
      gap = 0
      out.push(l)
    } else gap++
  })
  if (gap) out.push({ op: "gap", count: gap })
  return out
}

/** Comparable view of a revision (tracked columns + snapshot extras). */
export function revisionFields(r: AgentRevision): Record<string, string> {
  const s = r.snapshot ?? {}
  const show = (v: unknown) => (v === null || v === undefined || v === "" ? "—" : String(v))
  return {
    model: show(r.model_name ?? (r.model_id != null ? `#${r.model_id}` : null)),
    temperature: show(r.temperature),
    top_p: show(r.top_p),
    top_k: show(r.top_k),
    max_tokens: show(s.max_tokens),
    thinking_enabled: show(s.thinking_enabled),
    reasoning_effort: show(s.reasoning_effort),
    max_steps: show(s.max_steps),
    description: show(s.description),
  }
}

export interface FieldChange {
  field: string
  before: string
  after: string
}

export function revisionChanges(before: AgentRevision, after: AgentRevision): FieldChange[] {
  const a = revisionFields(before)
  const b = revisionFields(after)
  return Object.keys(b)
    .filter((k) => a[k] !== b[k])
    .map((k) => ({ field: k, before: a[k]!, after: b[k]! }))
}
